import React from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import {
  actionListboxButtonClassName,
  actionListboxOptionActiveClassName,
  actionTextClassName,
} from '@core/theme/actionTheme.util';

export interface ActionListboxOption {
  label: string;
  value: string;
}

interface ActionListboxFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: ActionListboxOption[];
  placeholder: string;
  noneOptionLabel?: string;
}

const ActionListboxField: React.FC<ActionListboxFieldProps> = ({
  id,
  value,
  onChange,
  options,
  placeholder,
  noneOptionLabel,
}) => {
  const selectedOption = options.find((option) => option.value === value);

  return (
    <Listbox value={value} onChange={onChange}>
      <div className="relative">
        <Listbox.Button id={id} className={actionListboxButtonClassName}>
          <span className="block truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </span>
        </Listbox.Button>
        <Transition
          as={React.Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
            {noneOptionLabel ? (
              <Listbox.Option
                className={({ active }) =>
                  `relative cursor-default py-2 pr-4 pl-10 select-none ${
                    active
                      ? actionListboxOptionActiveClassName
                      : 'text-gray-900'
                  }`
                }
                value=""
              >
                {({ selected }) => (
                  <>
                    <span
                      className={`block truncate ${
                        selected ? 'font-medium' : 'font-normal'
                      }`}
                    >
                      {noneOptionLabel}
                    </span>
                    {selected ? (
                      <span
                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${actionTextClassName}`}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ) : null}
            {options.map((option) => (
              <Listbox.Option
                key={option.value}
                className={({ active }) =>
                  `relative cursor-default py-2 pr-4 pl-10 select-none ${
                    active
                      ? actionListboxOptionActiveClassName
                      : 'text-gray-900'
                  }`
                }
                value={option.value}
              >
                {({ selected }) => (
                  <>
                    <span
                      className={`block truncate ${
                        selected ? 'font-medium' : 'font-normal'
                      }`}
                    >
                      {option.label}
                    </span>
                    {selected ? (
                      <span
                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${actionTextClassName}`}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
};

export default ActionListboxField;
